/**
 * PropertyCharts — colorful Recharts visualizations for the property
 * workspace intelligence tabs. Each export is a small, focused component
 * that accepts the data-shape its parent tab already loads.
 *
 * Design language: bold accent colors, tabular numbers, generous
 * whitespace. Tooltips use dark cards on light backgrounds. Every chart
 * degrades gracefully to a "no data yet" state.
 *
 * Charts included:
 *   1. DealScoreRadar         → radar / spider of the 5 scoring categories
 *   2. StrategyProfitBar      → colored bars of projected profit / cash flow
 *   3. CompsScatter           → scatter of comp sale prices with subject ARV
 *   4. RepairBreakdownDonut   → donut of repair cost by category
 *   5. CashFlowProjection     → 12-month cash-flow line chart
 *   6. ExitTimelineBar        → horizontal bars of days-to-exit × profit
 */
import {
  Radar, RadarChart, PolarGrid, PolarAngleAxis, PolarRadiusAxis,
  Bar, BarChart, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
  Cell, LabelList, Scatter, ScatterChart, ZAxis, ReferenceLine,
  Pie, PieChart, Line, LineChart, Area, ComposedChart,
} from "recharts";

// Bold, cohesive palette that reads well on white
const PALETTE = [
  "#10B981", // emerald
  "#3B82F6", // blue
  "#F59E0B", // amber
  "#EF4444", // rose
  "#8B5CF6", // violet
  "#06B6D4", // cyan
  "#F97316", // orange
  "#EC4899", // pink
];

const fmtMoney = (v) =>
  v == null || Number.isNaN(v) ? "—" :
  `$${Number(v).toLocaleString(undefined, { maximumFractionDigits: 0 })}`;

function EmptyChart({ label = "No data yet" }) {
  return (
    <div className="flex items-center justify-center h-48 text-sm text-slate-400 border border-dashed border-slate-200 rounded-lg bg-slate-50/50">
      {label}
    </div>
  );
}

function TooltipCard({ active, payload, label, prefix = "" }) {
  if (!active || !payload?.length) return null;
  return (
    <div className="rounded-lg border border-slate-200 bg-white/95 backdrop-blur-sm shadow-lg px-3 py-2 text-xs">
      {label != null && <div className="font-semibold text-slate-900 mb-1">{label}</div>}
      {payload.map((p, i) => (
        <div key={i} className="flex items-center gap-2 tabular-nums">
          <span className="inline-block w-2 h-2 rounded-full" style={{ background: p.color || p.fill }} />
          <span className="text-slate-600">{p.name}:</span>
          <span className="font-semibold text-slate-900">{prefix}{typeof p.value === "number" ? p.value.toLocaleString() : p.value}</span>
        </div>
      ))}
    </div>
  );
}

function CompsScatterTooltip({ active, payload }) {
  if (!active || !payload?.length) return null;
  const p = payload[0].payload;
  return (
    <div className="rounded-lg border border-slate-200 bg-white/95 shadow-lg px-3 py-2 text-xs">
      <div className="font-semibold text-slate-900 mb-1">{p.address}</div>
      <div className="text-slate-600 tabular-nums">
        {p.sqft.toLocaleString()} sqft · {fmtMoney(p.price)}
        {p.distance != null && <> · {Number(p.distance).toFixed(2)}mi</>}
      </div>
    </div>
  );
}

function DonutTooltipFactory(total) {
  function DonutTooltip({ active, payload }) {
    if (!active || !payload?.length) return null;
    const p = payload[0].payload;
    const pct = ((p.value / total) * 100).toFixed(1);
    return (
      <div className="rounded-lg border border-slate-200 bg-white/95 shadow-lg px-3 py-2 text-xs">
        <div className="flex items-center gap-2 mb-1">
          <span className="inline-block w-2 h-2 rounded-full" style={{ background: p.color }} />
          <span className="font-semibold text-slate-900 capitalize">{p.name}</span>
        </div>
        <div className="text-slate-600 tabular-nums">{fmtMoney(p.value)} · {pct}%</div>
      </div>
    );
  }
  return DonutTooltip;
}

function ExitTimelineTooltip({ active, payload }) {
  if (!active || !payload?.length) return null;
  const p = payload[0].payload;
  return (
    <div className="rounded-lg border border-slate-200 bg-white/95 shadow-lg px-3 py-2 text-xs">
      <div className="font-semibold text-slate-900 mb-1 capitalize">{p.strategy}</div>
      <div className="text-slate-600 tabular-nums">{p.days} days · {fmtMoney(p.profit)}</div>
    </div>
  );
}

// ─────────────────────── 1. Deal Score Radar ──────────────────────────
export function DealScoreRadar({ categories = [] }) {
  if (!categories?.length) return <EmptyChart label="Calculate the deal score to see the radar" />;
  const data = categories.map((c) => ({
    label: c.label || c.id,
    score: Math.round(c.score ?? c.capped_score ?? 0),
    fullMark: 100,
  }));
  return (
    <div data-testid="chart-deal-score-radar" className="w-full">
      <ResponsiveContainer width="100%" height={340}>
        <RadarChart data={data} outerRadius="72%">
          <PolarGrid stroke="#E2E8F0" />
          <PolarAngleAxis dataKey="label" tick={{ fill: "#475569", fontSize: 11, fontWeight: 600 }} />
          <PolarRadiusAxis domain={[0, 100]} tick={{ fill: "#94A3B8", fontSize: 10 }} axisLine={false} />
          <Radar
            name="Score"
            dataKey="score"
            stroke={PALETTE[0]}
            fill={PALETTE[0]}
            fillOpacity={0.35}
            strokeWidth={2}
          />
          <Tooltip content={<TooltipCard />} />
        </RadarChart>
      </ResponsiveContainer>
    </div>
  );
}

// ─────────────────────── 2. Strategy Profit Bar ────────────────────────
export function StrategyProfitBar({ ranked = [], winnerId = null }) {
  if (!ranked?.length) return <EmptyChart label="Run strategy comparison to see the ranking" />;
  const data = ranked.slice(0, 8).map((r, i) => {
    const raw = r.raw_factors || r;
    return {
      strategy: (r.label || r.display_name || r.strategy || r.id || `#${i + 1}`).replace(/_/g, " "),
      profit: Math.round(raw.projected_profit || 0),
      cashFlow: Math.round(raw.monthly_cash_flow || 0),
      isWinner: r.id === winnerId,
      color: r.id === winnerId ? PALETTE[0] : PALETTE[(i + 1) % PALETTE.length],
    };
  });
  return (
    <div data-testid="chart-strategy-profit" className="w-full">
      <ResponsiveContainer width="100%" height={340}>
        <BarChart data={data} margin={{ top: 8, right: 16, left: 8, bottom: 48 }}>
          <CartesianGrid strokeDasharray="3 3" stroke="#F1F5F9" vertical={false} />
          <XAxis
            dataKey="strategy"
            tick={{ fill: "#475569", fontSize: 11, fontWeight: 600 }}
            angle={-25}
            textAnchor="end"
            interval={0}
            height={60}
          />
          <YAxis
            tickFormatter={(v) => `$${(v / 1000).toFixed(0)}k`}
            tick={{ fill: "#94A3B8", fontSize: 11 }}
            axisLine={false}
            tickLine={false}
          />
          <Tooltip
            content={<TooltipCard prefix="$" />}
            cursor={{ fill: "#F8FAFC" }}
          />
          <Bar dataKey="profit" name="Projected Profit" radius={[8, 8, 0, 0]}>
            {data.map((d, i) => (
              <Cell key={i} fill={d.color} />
            ))}
          </Bar>
        </BarChart>
      </ResponsiveContainer>
    </div>
  );
}

// ─────────────────────── 3. Comps Scatter with Subject ARV ─────────────
export function CompsScatter({ comps = [], subjectArv = null, subjectSqft = null }) {
  if (!comps?.length) return <EmptyChart label="Load comps to see the ARV distribution" />;
  const data = comps
    .filter((c) => c.sale_price && c.sqft)
    .map((c) => ({
      sqft: Number(c.sqft),
      price: Number(c.sale_price),
      address: c.address || `Comp`,
      distance: c.distance_miles,
    }));
  if (!data.length) return <EmptyChart label="Comps missing sale price or sqft" />;
  return (
    <div data-testid="chart-comps-scatter" className="w-full">
      <ResponsiveContainer width="100%" height={340}>
        <ScatterChart margin={{ top: 12, right: 16, left: 8, bottom: 32 }}>
          <CartesianGrid strokeDasharray="3 3" stroke="#F1F5F9" />
          <XAxis
            type="number"
            dataKey="sqft"
            name="Sqft"
            tick={{ fill: "#94A3B8", fontSize: 11 }}
            axisLine={false}
            tickLine={false}
            label={{ value: "Square footage", position: "insideBottom", offset: -8, fill: "#64748B", fontSize: 11 }}
          />
          <YAxis
            type="number"
            dataKey="price"
            name="Sale Price"
            tickFormatter={(v) => `$${(v / 1000).toFixed(0)}k`}
            tick={{ fill: "#94A3B8", fontSize: 11 }}
            axisLine={false}
            tickLine={false}
          />
          <ZAxis range={[60, 60]} />
          <Tooltip content={CompsScatterTooltip} />
          <Scatter name="Comps" data={data} fill={PALETTE[1]} />
          {subjectArv != null && (
            <ReferenceLine
              y={Number(subjectArv)}
              stroke={PALETTE[0]}
              strokeWidth={2}
              strokeDasharray="5 3"
              label={{
                value: `Subject ARV ${fmtMoney(subjectArv)}`,
                position: "right",
                fill: PALETTE[0],
                fontSize: 11,
                fontWeight: 700,
              }}
            />
          )}
          {subjectSqft != null && (
            <ReferenceLine
              x={Number(subjectSqft)}
              stroke="#F59E0B"
              strokeDasharray="3 3"
              strokeWidth={1.5}
            />
          )}
        </ScatterChart>
      </ResponsiveContainer>
    </div>
  );
}

// ─────────────────────── 4. Repair Breakdown Donut ─────────────────────
export function RepairBreakdownDonut({ byCategory = {} }) {
  const entries = Object.entries(byCategory || {}).filter(([, v]) => Number(v) > 0);
  if (!entries.length) return <EmptyChart label="Add line items to see the cost breakdown" />;
  const total = entries.reduce((s, [, v]) => s + Number(v), 0);
  const data = entries.map(([cat, val], i) => ({
    name: cat.replace(/_/g, " "),
    value: Number(val),
    color: PALETTE[i % PALETTE.length],
  }));
  const DonutTooltip = DonutTooltipFactory(total);
  return (
    <div data-testid="chart-repair-donut" className="w-full">
      <ResponsiveContainer width="100%" height={280}>
        <PieChart>
          <Pie
            data={data}
            innerRadius={70}
            outerRadius={110}
            paddingAngle={2}
            dataKey="value"
            labelLine={false}
          >
            {data.map((d, i) => <Cell key={i} fill={d.color} stroke="none" />)}
          </Pie>
          <Tooltip content={DonutTooltip} />
        </PieChart>
      </ResponsiveContainer>
      <div className="flex flex-wrap gap-x-4 gap-y-1.5 mt-3 justify-center text-[11px] text-slate-600">
        {data.map((d, i) => (
          <div key={i} className="flex items-center gap-1.5">
            <span className="inline-block w-2.5 h-2.5 rounded-sm" style={{ background: d.color }} />
            <span className="capitalize">{d.name}</span>
            <span className="text-slate-400 tabular-nums">{fmtMoney(d.value)}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

// ─────────────────────── 5. Cash Flow Projection Line ──────────────────
export function CashFlowProjection({ monthlyCashFlow = 0, months = 12 }) {
  if (!monthlyCashFlow) return <EmptyChart label="Calculate rental analysis to project cash flow" />;
  const data = Array.from({ length: months }, (_, i) => {
    const monthly = Number(monthlyCashFlow);
    const cumulative = monthly * (i + 1);
    return {
      month: `M${i + 1}`,
      monthly,
      cumulative,
    };
  });
  const isPositive = monthlyCashFlow >= 0;
  return (
    <div data-testid="chart-cashflow-projection" className="w-full">
      <ResponsiveContainer width="100%" height={280}>
        <ComposedChart data={data} margin={{ top: 8, right: 16, left: 8, bottom: 8 }}>
          <defs>
            <linearGradient id="cfArea" x1="0" y1="0" x2="0" y2="1">
              <stop offset="5%" stopColor={isPositive ? PALETTE[0] : PALETTE[3]} stopOpacity={0.4} />
              <stop offset="95%" stopColor={isPositive ? PALETTE[0] : PALETTE[3]} stopOpacity={0} />
            </linearGradient>
          </defs>
          <CartesianGrid strokeDasharray="3 3" stroke="#F1F5F9" vertical={false} />
          <XAxis dataKey="month" tick={{ fill: "#94A3B8", fontSize: 11 }} axisLine={false} tickLine={false} />
          <YAxis
            tickFormatter={(v) => `$${(v / 1000).toFixed(1)}k`}
            tick={{ fill: "#94A3B8", fontSize: 11 }}
            axisLine={false}
            tickLine={false}
          />
          <Tooltip content={<TooltipCard prefix="$" />} />
          <Area
            type="monotone"
            dataKey="cumulative"
            name="Cumulative"
            stroke={isPositive ? PALETTE[0] : PALETTE[3]}
            strokeWidth={2.5}
            fill="url(#cfArea)"
          />
          <Line
            type="monotone"
            dataKey="monthly"
            name="Monthly"
            stroke={PALETTE[1]}
            strokeWidth={2}
            dot={{ r: 3, fill: PALETTE[1] }}
          />
        </ComposedChart>
      </ResponsiveContainer>
    </div>
  );
}

// ─────────────────────── 6. Exit Timeline Bar ──────────────────────────
export function ExitTimelineBar({ strategies = [], recommended = null }) {
  if (!strategies?.length) return <EmptyChart label="Run the exit matrix to see strategy timelines" />;
  const data = strategies
    .filter((s) => s.math)
    .slice(0, 8)
    .map((s, i) => ({
      strategy: (s.label || s.display_name || s.strategy || `#${i + 1}`).replace(/_/g, " "),
      days: s.math?.timeline_days || 0,
      profit: s.math?.projected_profit || s.math?.net_profit || s.math?.total_profit || 0,
      isRec: s.strategy === recommended,
    }));
  return (
    <div data-testid="chart-exit-timeline" className="w-full">
      <ResponsiveContainer width="100%" height={Math.max(220, data.length * 44)}>
        <BarChart data={data} layout="vertical" margin={{ top: 8, right: 32, left: 8, bottom: 8 }}>
          <CartesianGrid strokeDasharray="3 3" stroke="#F1F5F9" horizontal={false} />
          <XAxis
            type="number"
            tick={{ fill: "#94A3B8", fontSize: 11 }}
            axisLine={false}
            tickLine={false}
            label={{ value: "Days to exit", position: "insideBottom", offset: -2, fill: "#64748B", fontSize: 11 }}
          />
          <YAxis
            type="category"
            dataKey="strategy"
            tick={{ fill: "#475569", fontSize: 11, fontWeight: 600 }}
            axisLine={false}
            tickLine={false}
            width={120}
          />
          <Tooltip content={ExitTimelineTooltip} />
          <Bar dataKey="days" name="Days to exit" radius={[0, 8, 8, 0]}>
            {data.map((d, i) => (
              <Cell key={i} fill={d.isRec ? PALETTE[0] : PALETTE[(i + 2) % PALETTE.length]} />
            ))}
            <LabelList
              dataKey="days"
              position="right"
              formatter={(v) => `${v}d`}
              style={{ fill: "#64748B", fontSize: 11, fontWeight: 600 }}
            />
          </Bar>
        </BarChart>
      </ResponsiveContainer>
    </div>
  );
}
